/**
 * Language metadata registry — auto-detect flag, native name, RTL, default currency from ISO 639-1 code.
 * Used by the dynamic language switcher and admin translation panel.
 */

export interface LanguageMeta {
  code: string;
  nativeName: string;
  englishName: string;
  flag: string;
  isRTL: boolean;
  defaultCurrency: string;
}

export const LANGUAGE_META: Record<string, LanguageMeta> = {
  en: { code: 'en', nativeName: 'English', englishName: 'English', flag: '🇬🇧', isRTL: false, defaultCurrency: 'USD' },
  nl: { code: 'nl', nativeName: 'Nederlands', englishName: 'Dutch', flag: '🇳🇱', isRTL: false, defaultCurrency: 'EUR' },
  de: { code: 'de', nativeName: 'Deutsch', englishName: 'German', flag: '🇩🇪', isRTL: false, defaultCurrency: 'EUR' },
  fr: { code: 'fr', nativeName: 'Français', englishName: 'French', flag: '🇫🇷', isRTL: false, defaultCurrency: 'EUR' },
  es: { code: 'es', nativeName: 'Español', englishName: 'Spanish', flag: '🇪🇸', isRTL: false, defaultCurrency: 'EUR' },
  it: { code: 'it', nativeName: 'Italiano', englishName: 'Italian', flag: '🇮🇹', isRTL: false, defaultCurrency: 'EUR' },
  pt: { code: 'pt', nativeName: 'Português', englishName: 'Portuguese', flag: '🇵🇹', isRTL: false, defaultCurrency: 'EUR' },
  sv: { code: 'sv', nativeName: 'Svenska', englishName: 'Swedish', flag: '🇸🇪', isRTL: false, defaultCurrency: 'SEK' },
  pl: { code: 'pl', nativeName: 'Polski', englishName: 'Polish', flag: '🇵🇱', isRTL: false, defaultCurrency: 'PLN' },
  ja: { code: 'ja', nativeName: '日本語', englishName: 'Japanese', flag: '🇯🇵', isRTL: false, defaultCurrency: 'JPY' },
  ko: { code: 'ko', nativeName: '한국어', englishName: 'Korean', flag: '🇰🇷', isRTL: false, defaultCurrency: 'KRW' },
  ru: { code: 'ru', nativeName: 'Русский', englishName: 'Russian', flag: '🇷🇺', isRTL: false, defaultCurrency: 'EUR' },
  tr: { code: 'tr', nativeName: 'Türkçe', englishName: 'Turkish', flag: '🇹🇷', isRTL: false, defaultCurrency: 'TRY' },
  da: { code: 'da', nativeName: 'Dansk', englishName: 'Danish', flag: '🇩🇰', isRTL: false, defaultCurrency: 'EUR' },
  no: { code: 'no', nativeName: 'Norsk', englishName: 'Norwegian', flag: '🇳🇴', isRTL: false, defaultCurrency: 'NOK' },
  fi: { code: 'fi', nativeName: 'Suomi', englishName: 'Finnish', flag: '🇫🇮', isRTL: false, defaultCurrency: 'EUR' },
  cs: { code: 'cs', nativeName: 'Čeština', englishName: 'Czech', flag: '🇨🇿', isRTL: false, defaultCurrency: 'EUR' },
  // Extended set
  ar: { code: 'ar', nativeName: 'العربية', englishName: 'Arabic', flag: '🇸🇦', isRTL: true, defaultCurrency: 'USD' },
  he: { code: 'he', nativeName: 'עברית', englishName: 'Hebrew', flag: '🇮🇱', isRTL: true, defaultCurrency: 'ILS' },
  zh: { code: 'zh', nativeName: '中文', englishName: 'Chinese', flag: '🇨🇳', isRTL: false, defaultCurrency: 'CNY' },
  hi: { code: 'hi', nativeName: 'हिन्दी', englishName: 'Hindi', flag: '🇮🇳', isRTL: false, defaultCurrency: 'INR' },
  th: { code: 'th', nativeName: 'ไทย', englishName: 'Thai', flag: '🇹🇭', isRTL: false, defaultCurrency: 'THB' },
  vi: { code: 'vi', nativeName: 'Tiếng Việt', englishName: 'Vietnamese', flag: '🇻🇳', isRTL: false, defaultCurrency: 'VND' },
  id: { code: 'id', nativeName: 'Bahasa Indonesia', englishName: 'Indonesian', flag: '🇮🇩', isRTL: false, defaultCurrency: 'IDR' },
  uk: { code: 'uk', nativeName: 'Українська', englishName: 'Ukrainian', flag: '🇺🇦', isRTL: false, defaultCurrency: 'UAH' },
  el: { code: 'el', nativeName: 'Ελληνικά', englishName: 'Greek', flag: '🇬🇷', isRTL: false, defaultCurrency: 'EUR' },
  ro: { code: 'ro', nativeName: 'Română', englishName: 'Romanian', flag: '🇷🇴', isRTL: false, defaultCurrency: 'RON' },
  hu: { code: 'hu', nativeName: 'Magyar', englishName: 'Hungarian', flag: '🇭🇺', isRTL: false, defaultCurrency: 'HUF' },
  bg: { code: 'bg', nativeName: 'Български', englishName: 'Bulgarian', flag: '🇧🇬', isRTL: false, defaultCurrency: 'BGN' },
  sk: { code: 'sk', nativeName: 'Slovenčina', englishName: 'Slovak', flag: '🇸🇰', isRTL: false, defaultCurrency: 'EUR' },
  sl: { code: 'sl', nativeName: 'Slovenščina', englishName: 'Slovenian', flag: '🇸🇮', isRTL: false, defaultCurrency: 'EUR' },
  hr: { code: 'hr', nativeName: 'Hrvatski', englishName: 'Croatian', flag: '🇭🇷', isRTL: false, defaultCurrency: 'EUR' },
  lt: { code: 'lt', nativeName: 'Lietuvių', englishName: 'Lithuanian', flag: '🇱🇹', isRTL: false, defaultCurrency: 'EUR' },
  lv: { code: 'lv', nativeName: 'Latviešu', englishName: 'Latvian', flag: '🇱🇻', isRTL: false, defaultCurrency: 'EUR' },
  et: { code: 'et', nativeName: 'Eesti', englishName: 'Estonian', flag: '🇪🇪', isRTL: false, defaultCurrency: 'EUR' },
};

/** Lookup with safe fallback for unknown ISO codes. */
export function getLanguageMeta(code: string): LanguageMeta {
  const norm = code.toLowerCase().split('-')[0];
  return (
    LANGUAGE_META[norm] ?? {
      code: norm,
      nativeName: norm.toUpperCase(),
      englishName: norm.toUpperCase(),
      flag: '🌐',
      isRTL: false,
      defaultCurrency: 'EUR',
    }
  );
}

export const SUPPORTED_LANGUAGE_CODES = Object.keys(LANGUAGE_META);

export {
  TRANSLATION_NAMESPACES,
  type TranslationNamespace,
} from "../../shared/translation-config";
